// src/components/Sidebar.js — merged portal top nav bar.
// Redesign pass: emoji-as-icon removed (reads unprofessional at this
// density), 14 tabs grouped into 4 logical clusters instead of one flat
// scrolling row, active state is a solid filled pill rather than a
// lighter tint so it's unambiguous at a glance.
//
// Permission filtering: each nav item maps to a Team Management
// permission category (see PERMISSION_CATEGORIES in pages/Teams.js).
// Owner and Team Leader always see everything, matching the same
// server-side bypass in middleware/auth.js — everyone else only sees
// tabs their checked categories actually cover. Dashboard/Overview is
// always visible to everyone regardless of permissions, since it's the
// landing page and has nothing sensitive on it.
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { get } from '../utils/api';

// path -> permission category required to see this item. Omit a path
// here (or leave it out of GROUPS filtering below) to make it always
// visible regardless of permissions.
const NAV_PERMISSION_MAP = {
  '/dashboard': null, // always visible
  '/messages': null, // always visible
  '/heatmap':        'reports',
  '/live-map':        'dispatch',
  '/dispatch':        'dispatch',
  '/warehouse':        'warehouse_ops',
  '/assign':        'dispatch',
  '/vouchers':        'vouchers',
  '/return-labels': 'checking',
  '/teams':        'admin_settings',
  '/transports':        'admin_settings',
  '/reports':        'reports',
  '/settings':        'admin_settings',
  '/location-approvals': 'admin_settings',
  '/inward': 'inward_register',
};

const GROUPS = [
  {
    label: 'Overview',
    items: [
      { path: '/dashboard', label: 'Dashboard' },
      { path: '/messages',  label: 'Messages' },
      { path: '/heatmap',   label: 'Heatmap' },
      { path: '/live-map',  label: 'Live Map' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { path: '/dispatch',       label: 'Dispatch' },
      { path: '/warehouse',      label: 'Warehouse' },
      { path: '/assign',         label: 'Assign Deliveries' },
      { path: '/vouchers',       label: 'Vouchers' },
      { path: '/return-labels', label: 'Return Labels' },
      { path: '/inward', label: 'Inward Register' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { path: '/teams',      label: 'Team Management' },
      { path: '/transports', label: 'Transports' },
      { path: '/reports',    label: 'Reports' },
      { path: '/settings',   label: 'Settings' },
      { path: '/location-approvals', label: 'Location Approvals' },
    ],
  },
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('dms_user') || '{}');
  const { theme, toggleTheme } = useTheme();

  const isAdminBypass = ['owner', 'team_leader'].includes(user.role);
  const isPlatformAdmin = user.isPlatformAdmin === true;
  const perms = user.permissions || {};

  const [unreadCount, setUnreadCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // New-message cards (bottom right corner)
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    let alive = true;
    let first = true;
    const seen = new Set();
    function previewOf(m) {
      if (m.content && String(m.content).trim()) return String(m.content).trim().slice(0, 120);
      const at = String(m.attachment_type || '');
      if (at.includes('image')) return 'Photo';
      if (at.includes('voice') || at.includes('audio')) return 'Voice note';
      return at ? 'Attachment' : '';
    }
    let lastUnread = -1;
    function check() {
      get('/api/messages/unread-count').then(c => {
        const n = (c && c.unread) || 0;
        const rose = first || n > lastUnread;
        lastUnread = n;
        return rose ? get('/api/messages/inbox') : null; // inbox is downloaded only when something new arrived
      }).then(d => {
        if (!alive || !d) return;
        const unread = (d.messages || []).filter(m => !m.is_read);
        const fresh = unread.filter(m => !seen.has(m.id));
        unread.forEach(m => seen.add(m.id));
        if (first) { first = false; return; }
        if (!fresh.length) return;
        if (window.location.pathname.startsWith('/messages')) return;
        const cards = fresh.slice(0, 3).map(m => ({
          id: m.id,
          sender: m.sender_name || 'New message',
          bill: m.invoice_number ? 'Bill ' + m.invoice_number : '',
          preview: previewOf(m),
        }));
        setToasts(list => [...cards, ...list].slice(0, 3));
        cards.forEach(c => setTimeout(() => setToasts(list => list.filter(x => x.id !== c.id)), 8000));
      }).catch(() => {});
    }
    check();
    const iv = setInterval(() => { if (!document.hidden) check(); }, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, []);
  useEffect(() => {
    function loadUnread() {
      get('/api/messages/unread-count').then(d => setUnreadCount(d.unread || 0)).catch(() => {});
    }
    loadUnread();
    const interval = setInterval(() => { if (!document.hidden) loadUnread(); }, 30000);
    const onVisible = () => { if (!document.hidden) loadUnread(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  function canSee(path) {
    if (isAdminBypass) return true;
    if (path === '/warehouse') { return perms.stock_removal === true || perms.checking === true || (perms.warehouse_ops === true && perms.stock_removal === undefined && perms.checking === undefined); }
    const category = NAV_PERMISSION_MAP[path];
    if (!category) return true; // no category required — always visible
    return perms[category] === true;
  }

  const visibleGroups = GROUPS
    .map(group => ({ ...group, items: group.items.filter(item => canSee(item.path)) }))
    .filter(group => group.items.length > 0);

  // Platform Admin link — deliberately NOT part of the normal
  // GROUPS/canSee() permission system above, which auto-bypasses for
  // ANY owner or team_leader. This has to stay invisible to a future
  // client's own owner, so it's gated purely on isPlatformAdmin, set
  // directly in the database on RJ's account only.
  const navGroups = isPlatformAdmin
    ? [...visibleGroups, { label: 'Platform', items: [{ path: '/platform-admin', label: 'Platform Admin' }] }]
    : visibleGroups;

  function logout() {
    localStorage.removeItem('dms_token');
    localStorage.removeItem('dms_user');
    navigate('/');
  }

  return (
    <header className="sticky top-0 z-50 bg-brand-700 text-white shadow-sm" style={{ width: '100%' }}>
      {/* Mobile bar (phones only) */}
      <div className="md:hidden flex items-center justify-between gap-2 px-3 py-2 border-b border-white/10">
        <div className="flex items-center gap-2 min-w-0">
          <img src="/medji-icon.png" alt="Medji" className="w-7 h-7 rounded-lg" />
          <span className="text-sm font-semibold">Medji</span>
          <span className="text-sm text-white/70 truncate">
            {'· ' + (navGroups.flatMap(g => g.items).find(i => location.pathname.startsWith(i.path)) || { label: '' }).label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            title="Toggle theme"
            className="w-9 h-9 flex items-center justify-center text-white/70 border border-white/15 rounded-md"
          >
            {theme === 'dark' ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
            )}
          </button>
          <button
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Menu"
            className="relative w-9 h-9 flex items-center justify-center text-white border border-white/20 rounded-md"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {menuOpen ? <path d="M6 6l12 12M18 6L6 18"/> : <path d="M3 6h18M3 12h18M3 18h18"/>}
            </svg>
            {unreadCount > 0 && !menuOpen && <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 rounded-full border-2 border-brand-700" />}
          </button>
        </div>
      </div>

      <div className="hidden md:flex items-center justify-between gap-4 px-6 py-3 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <img src="/medji-icon.png" alt="Medji" className="w-8 h-8 rounded-lg" />
          <div className="flex items-baseline gap-2">
            <h1 className="text-sm font-semibold tracking-wide">Medji</h1>
            <span className="text-xs text-white/60">Delivery Management</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right leading-tight">
            <p className="text-sm font-medium">{user.name || 'User'}</p>
            <p className="text-[11px] text-white/60 capitalize">{(user.role || '').replace('_', ' ')}</p>
          </div>
          <button
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="w-8 h-8 flex items-center justify-center text-white/70 border border-white/15 rounded-md hover:bg-white/10 hover:text-white transition-colors"
          >
            {theme === 'dark' ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
            )}
          </button>
          <button
            onClick={logout}
            className="px-3 py-1.5 text-xs font-medium text-white/70 border border-white/15 rounded-md hover:bg-white/10 hover:text-white transition-colors"
          >
            Log out
          </button>
        </div>
      </div>

      <nav className="hidden md:flex flex-wrap items-center gap-x-5 gap-y-1.5 px-6 py-2.5">
        {navGroups.map((group, gi) => (
          <div key={group.label} className="flex items-center gap-1">
            {gi > 0 && <span className="hidden sm:block w-px h-4 bg-white/10 mr-4" />}
            {group.items.map(item => {
              const active = location.pathname.startsWith(item.path);
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`relative px-2.5 py-1.5 rounded-md text-[13px] font-medium whitespace-nowrap transition-colors
                    ${active ? 'bg-accent-500 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}
                >
                  {item.label}
                  {item.path === '/messages' && unreadCount > 0 && (
                    <span className="absolute -top-2.5 -right-2.5 bg-red-500 text-white text-xs font-bold rounded-full min-w-[22px] h-[22px] px-1 flex items-center justify-center ring-2 ring-brand-700">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Mobile menu panel (phones only) */}
      {menuOpen && (
        <div className="md:hidden bg-brand-700 border-t border-white/10" style={{ maxHeight: 'calc(100vh - 56px)', overflowY: 'auto' }}>
          {navGroups.map(group => (
            <div key={group.label} className="px-3 pt-3">
              <p className="px-2 pb-1 text-[11px] uppercase tracking-wider text-white/40">{group.label}</p>
              {group.items.map(item => {
                const active = location.pathname.startsWith(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`w-full flex items-center justify-between px-3 py-3 rounded-md text-[15px] font-medium text-left
                      ${active ? 'bg-accent-500 text-white' : 'text-white/80'}`}
                  >
                    <span>{item.label}</span>
                    {item.path === '/messages' && unreadCount > 0 && (
                      <span className="bg-red-500 text-white text-sm font-bold rounded-full min-w-[26px] h-[26px] px-1.5 flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
          <div className="flex items-center justify-between gap-3 px-5 py-4 mt-3 border-t border-white/10">
            <div className="leading-tight">
              <p className="text-sm font-medium">{user.name || 'User'}</p>
              <p className="text-[11px] text-white/60 capitalize">{(user.role || '').replace('_', ' ')}</p>
            </div>
            <button
              onClick={logout}
              className="px-4 py-2 text-sm font-medium text-white/80 border border-white/20 rounded-md whitespace-nowrap"
            >
              Log out
            </button>
          </div>
        </div>
      )}

      {/* New-message cards (bottom right) */}
      {toasts.length > 0 && (
        <div style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 70, display: 'flex', flexDirection: 'column', gap: 8, width: 320, maxWidth: 'calc(100vw - 32px)' }}>
          {toasts.map(t => (
            <div
              key={t.id}
              onClick={() => { setToasts(list => list.filter(x => x.id !== t.id)); navigate('/messages'); }}
              className="cursor-pointer rounded-lg shadow-2xl bg-accent-500 text-white px-3 py-2.5"
              style={{ border: '2px solid #ffffff', boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold truncate">{t.sender}</p>
                <button
                  onClick={e => { e.stopPropagation(); setToasts(list => list.filter(x => x.id !== t.id)); }}
                  aria-label="Dismiss"
                  className="text-white/80 hover:text-white text-lg leading-none"
                >&times;</button>
              </div>
              {t.bill && <p className="text-[11px] text-white/80">{t.bill}</p>}
              <p className="text-sm text-white mt-0.5" style={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{t.preview}</p>
            </div>
          ))}
        </div>
      )}
    </header>
  );
}
